import { WakeUp } from 'common/interface/props';
import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import { useForm, UseFormHandleSubmit } from 'react-hook-form';

// atomic component
import Container from 'components/UI/atoms/Container';
import Text from 'components/UI/atoms/Text';
import Grid from 'components/UI/atoms/Grid';
import Button from 'components/UI/atoms/Button';
import TextField from 'components/UI/atoms/TextField';
import Table from 'components/UI/atoms/Table';
import Select from 'components/UI/atoms/Select';
import Pagination from 'components/UI/molecules/Pagination';

import { useMutation, useQuery } from 'react-query';
import { GetTrackingListInput, SearchStatusType, SearchType } from 'components/form/interfaces/user-admin.interface';
import { getTrackingList, runAllJobs, runSelectedProd, requestCountSelect, requestCountAll } from 'components/form/api/user-admin.api';
import { USER_ADMIN_CONFIG } from 'components/user-admin/config/user-admin.config';
import { UserAdminConfig } from 'components/user-admin/interfaces/user-admin.interface';

interface IUserAdminDashboard {
  solType: WakeUp;
}

const ButtonTag = styled(Button)`
  border: 1px solid #e2e2e2;
  padding: 5px 5px;

  display: inline-flex;
  justify-content: center;
  align-items: center;
  :hover {
    background: #f5f5f5;
  }
`;

export const StyledExcelButton = styled(ButtonTag)`
  display: inline-flex;
  align-items: center;
  margin-left: 5px;
  gap: 2px;
  padding: 4px 6px;
  font-size: 0.85rem;
  color: #000;
  width: auto;
  min-width: unset;
  &:hover {
    background: #f5f5f5;
  }
`;

const RefetchTimeContainer = styled(Grid)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
`;

export default function UserAdminDashboard({ solType }: IUserAdminDashboard) {
  const gmptoken = JSON.parse(sessionStorage.getItem('wakeupInfo') || '{}').gmpToken;
  const config: UserAdminConfig | null = USER_ADMIN_CONFIG[solType];

  // 모든 Hook은 조건문 이전에 호출되어야 함
  const { register, handleSubmit, watch, setValue } = useForm<GetTrackingListInput>({
    defaultValues: {
      searchStatus: SearchStatusType.ALL,
      searchType: SearchType.SCRAP_PROD_CODE,
      searchValue: '',
      nowPage: 1,
      viewCount: 30,
    },
  });

  // 폼 값 개별 관찰 (watch()는 매 렌더링마다 새 객체를 생성하므로 개별 watch 사용)
  const searchStatus = watch('searchStatus');
  const nowPage = watch('nowPage');
  const viewCount = watch('viewCount');
  // 검색어는 검색 버튼 누를 때만 반영될 수 있도록 queryKey 에 관리할 스테이트 생성
  const [searchQuery, setSearchQuery] = useState<{ searchType: SearchType; searchValue: string }>({
    searchType: SearchType.SCRAP_PROD_CODE,
    searchValue: '',
  });
  // 선택된 행의 ID 배열
  const [selectedRowIds, setSelectedRowIds] = useState<(string | number)[]>([]);
  // 마지막 리페치 시간
  const [lastRefetchTime, setLastRefetchTime] = useState<Date | null>(null);
  // 리페치 경과 시간 표시
  const [refetchElapsedTime, setRefetchElapsedTime] = useState<string>('방금전');

  /**
   * @alias 연동중인 스케줄러 상품리스트 조회
   */
  const {
    data: trackingListResponse,
    isLoading: isTrackingListLoading,
    isFetching: isTrackingListFetching,
    refetch: refetchTrackingList,
  } = useQuery({
    queryKey: ['trackingList', searchStatus, nowPage, viewCount, searchQuery],
    queryFn: () => {
      const queryParams: GetTrackingListInput = {
        nowPage,
        viewCount,
      };

      // 옵셔널 필드 추가
      if (searchStatus && searchStatus !== SearchStatusType.ALL) {
        queryParams.searchStatus = searchStatus;
      }
      if (searchQuery) {
        queryParams.searchType = searchQuery.searchType;
        queryParams.searchValue = searchQuery.searchValue;
      }

      return getTrackingList(queryParams);
    },
    enabled: !!gmptoken,
    refetchOnWindowFocus: false,
    refetchInterval: 30000, // 30초마다 자동 재호출
    refetchIntervalInBackground:true, // 백그라운드에서도 재호출 가능하도록 설정 (화면 포커스 아닐 때도 재호출)
    keepPreviousData: true,
    retry: false,
    onSuccess: () => {
      // 리페치 성공 시 마지막 리페치 시간 업데이트
      setLastRefetchTime(new Date());
    },
  });

  /**
   * @alias 초기 데이터 로드 시 마지막 리페치 시간 설정
   */
  useEffect(() => {
    if (trackingListResponse && !lastRefetchTime) {
      setLastRefetchTime(new Date());
    }
  }, [trackingListResponse, lastRefetchTime]);

  const trackingList = trackingListResponse?.result || [];
  const totalCount = trackingListResponse?.totalLength || 0;
  const maxPage = Math.ceil(totalCount / viewCount);

  /**
   * @alias 리페치 경과 시간 계산 및 업데이트
   */
  useEffect(() => {
    if (!lastRefetchTime) return;

    const updateElapsedTime = () => {
      const now = new Date();
      const elapsedSeconds = Math.floor((now.getTime() - lastRefetchTime.getTime()) / 1000);

      if (elapsedSeconds < 10) {
        setRefetchElapsedTime('방금 전');
      } else if (elapsedSeconds < 20) {
        setRefetchElapsedTime('10초 전');
      } else if (elapsedSeconds < 30) {
        setRefetchElapsedTime('20초 전');
      } else {
        // 30초가 지나면 다음 리페치를 기다리는 상태
        setRefetchElapsedTime('방금 전');
      }
    };

    // 즉시 업데이트
    updateElapsedTime();

    // 10초마다 업데이트 (텍스트 변경 빈도 줄이기)
    const interval = setInterval(updateElapsedTime, 10000);

    return () => clearInterval(interval);
  }, [lastRefetchTime]);

  /**
   * @alias 검색 제출 핸들러
   */
  const onSubmit = (data: GetTrackingListInput) => {
    setValue('nowPage', 1); // 검색 시 첫 페이지로 이동
    setSearchQuery({
      searchType: data.searchType || SearchType.SCRAP_PROD_CODE,
      searchValue: data.searchValue || '',
    });
  };

  /**
   * @alias 상품별 업데이트 뮤테이트
   */
  const { mutate: runSelectedProdMutate } = useMutation({
    mutationKey: ['runSelectedProd'],
    mutationFn: runSelectedProd,
    onMutate: () => {
      Swal.fire({
        title: '잠시만 기다려 주세요.',
        html: '업데이트 요청 중...',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    },
    onSuccess: (_) => {
      refetchTrackingList();
      setSelectedRowIds([]);
      Swal.fire({
        icon: 'success',
        title: '성공',
        html: `업데이트가 정상적으로 요청되었습니다.<br/>(단, 실제 반영까지는 다소 시간이 소요될 수 있습니다.)`,
        confirmButtonText: '확인',
      });
    },
    onError: (error: any) => {
      Swal.fire({
        icon: 'error',
        title: '실패',
        // html: `${error.message || '알 수 없는 오류'}`,
        html: `업데이트에 실패하였습니다. 실패 사유 확인 후 네이버 스마트스토어에서 수정 및 다시 시도해주세요.`,
        confirmButtonText: '확인',
      });
    },
  });

  /**
   * @alias 일괄 업데이트 뮤테이트
   */
  const { mutate: runAllJobsMutate } = useMutation({
    mutationKey: ['runAllJobs'],
    mutationFn: runAllJobs,
    onMutate: () => {
      Swal.fire({
        title: '잠시만 기다려 주세요.',
        html: '업데이트 요청 중...',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    },
    onSuccess: (_) => {
      refetchTrackingList();
      setSelectedRowIds([]);
      Swal.fire({
        icon: 'success',
        title: '성공',
        html: `업데이트가 정상적으로 요청되었습니다.<br/>(단, 실제 반영까지는 다소 시간이 소요될 수 있습니다.)`,
        confirmButtonText: '확인',
      });
    },
    onError: (error: any) => {
      Swal.fire({
        icon: 'error',
        title: '실패',
        html: `업데이트에 실패하였습니다. 실패 사유 확인 후 네이버 스마트스토어에서 수정 및 다시 시도해주세요.`,
        confirmButtonText: '확인',
      });
    },
  });

  /**
   * @alias 업데이트 요청 카운트 조회 뮤테이트
   */
  const { mutateAsync: requestCountSelectAsync } = useMutation({
    mutationKey: ['requestCountSelect'],
    mutationFn: requestCountSelect,
  });

  /**
   * @alias 일괄 업데이트 횟수 조회 쿼리
   */
  const { data: requestCountAllData } = useQuery({
    queryKey: ['requestCountAll'],
    queryFn: requestCountAll,
  });

  /**
   * @alias 상품별 업데이트 요청 횟수 체크 헬퍼 함수
   * @description jobList를 받아서 각 상품의 업데이트 요청 횟수를 조회하고 검증합니다.
   * - isValid: 모든 상품이 업데이트 가능한 횟수를 가지고 있는지 여부
   * - counts: 각 상품의 wakeupProdCode를 키로 하는 요청 횟수 Map
   */
  const checkRequestCounts = async (jobList: any[]): Promise<{ isValid: boolean; counts: Map<string, number> }> => {
    const counts = new Map<string, number>();
    let isValid = true;

    try {
      const { success, resultList } = await requestCountSelectAsync({ jobList });

      if (success && resultList && resultList.length > 0) {
        for (const item of resultList) {
          const count = item.requestCount || 0;
          // const count = Math.random() < 0.5 ? 4 : 5; (테스트용)
          counts.set(item.wakeupProdCode, count);

          // 일 5회 이상 업데이트 요청 불가
          if (count >= 5) {
            isValid = false;
          }
        }
      }
    } catch (error) {}

    return { isValid, counts };
  };

  /**
   * @alias 일괄 업데이트 이벤트
   * @description
   * 1. 선택하지 않고 누르면 전체 일괄 업데이트
   * 2. 선택하고 누르면 선택한 것들만 업데이트
   */
  const handleBatchUpdate = async () => {
    // 1. 선택하지 않았을 때 : 전체 상품에 대한 업데이트
    if (selectedRowIds.length === 0) {
      // 1-1. 횟수 남아있는지 확인 (일 1회)
      if ((requestCountAllData?.requestCount || 0) >= 1) {
        Swal.fire({
          icon: 'error',
          title: '실패',
          html: `일괄 업데이트 횟수를 모두 사용하였습니다.<br/>자정 이후 다시 시도해주세요.`,
          confirmButtonText: '확인',
        });
        return;
      }

      // 1-2. 횟수 남아있으면 진행
      const result = await Swal.fire({
        icon: 'question',
        title: '일괄 업데이트',
        html: `전체 상품에 대해 일괄 업데이트를 진행하시겠습니까?`,
        showCancelButton: true,
        cancelButtonText: '취소',
        confirmButtonText: '확인',
      });

      if (result.isConfirmed) {
        // 일괄 업데이트 API 호출
        runAllJobsMutate();
      }
    } else {
      // 2. 선택한 것들만 업데이트

      // 2-1. 선택된 상품 중 업데이트 가능한 상품만 필터링
      // selectedRowIds에 있는 listId를 기준으로 trackingList에서 필요한 데이터 추출
      const jobList = trackingList
        .filter((product) => selectedRowIds.includes(product.listId) && product.status !== SearchStatusType.REGIST_FAILURE)
        .map((product) => ({
          schedulerDetailId: product.schedulerDetailId,
          wakeupProdCode: product.wakeupProdCode,
          scrapProdCode: product.scrapProdCode,
        }));

      if (jobList.length === 0) {
        await Swal.fire({
          icon: 'info',
          title: '알림',
          html: '선택한 상품 중 업데이트 가능한 상품이 없습니다.',
          confirmButtonText: '확인',
        });
        return;
      }

      // 2-2. 횟수 남아있는지 확인
      const { isValid, counts } = await checkRequestCounts(jobList);

      // 횟수가 남은 상품과 초과한 상품 분리
      const validProducts: typeof jobList = [];
      const invalidProducts: typeof jobList = [];

      for (const job of jobList) {
        const count = counts.get(job.wakeupProdCode) || 0;
        if (count >= 5) {
          invalidProducts.push(job);
        } else {
          validProducts.push(job);
        }
      }

      // 모든 상품이 횟수 초과한 경우
      if (validProducts.length === 0) {
        await Swal.fire({
          icon: 'error',
          title: '실패',
          html: `선택한 모든 상품이 업데이트 횟수를 초과하였습니다.<br/>자정 이후 다시 시도해주세요.`,
          confirmButtonText: '확인',
        });
        return;
      }

      // 일부 상품만 횟수 초과한 경우
      let confirmMessage = '';
      if (!isValid && invalidProducts.length > 0) {
        confirmMessage = `선택한 상품 중 ${invalidProducts.length}건은 업데이트 횟수를 초과하였습니다.<br/>${validProducts.length}건만 업데이트를 진행하시겠습니까?`;
      } else {
        confirmMessage = `선택된 상품 중 업데이트 가능한 ${validProducts.length}건의 상품을 업데이트 진행하시겠습니까?`;
      }

      const result = await Swal.fire({
        icon: 'question',
        title: '선택 업데이트',
        html: confirmMessage,
        showCancelButton: true,
        cancelButtonText: '취소',
        confirmButtonText: '확인',
      });

      // 일괄 업데이트 진행
      if (result.isConfirmed) {
        // 횟수가 남은 상품만 전달
        runSelectedProdMutate({ jobList: validProducts });
      }
    }
  };

  /**
   * @alias 단건 즉시 업데이트 이벤트
   * @description 즉시 업데이트 버튼을 통해 1건만 업데이트 진행
   */
  const handleSingleUpdate = async (params: any, isDisabled: boolean) => {
    if (isDisabled) return;

    const jobItem = {
      schedulerDetailId: params.value?.schedulerDetailId,
      wakeupProdCode: params.value?.wakeupProdCode,
      scrapProdCode: params.value?.scrapProdCode,
    };

    // 1. 요청 전, 횟수 체크
    const { isValid, counts } = await checkRequestCounts([jobItem]);
    const count = counts.get(jobItem.wakeupProdCode) || 0;

    // 일 5회 이상 업데이트 요청 불가
    if (!isValid) {
      Swal.fire({
        icon: 'error',
        title: '실패',
        html: `해당 상품의 업데이트 횟수를 모두 사용하셨습니다.<br/>자정 이후 다시 시도해주세요.`,
        confirmButtonText: '확인',
      });
      return;
    }

    // 2. 요청 진행
    const confirm = await Swal.fire({
      icon: 'question',
      title: '즉시 업데이트',
      html: count < 5 ? `해당 상품의 업데이트 잔여 횟수는 ${5 - count}회 남았습니다.<br/>업데이트를 진행하시겠습니까?` : `업데이트를 진행하시겠습니까?`,
      showCancelButton: true,
      cancelButtonText: '취소',
      confirmButtonText: '확인',
    });

    if (confirm.isConfirmed) {
      // API 호출 - 배열에 하나의 아이템만 담아서 호출
      runSelectedProdMutate({
        jobList: [jobItem],
      });
    }
  };

  // config가 없으면 early return
  if (!config) {
    return (
      <Container noBody={true}>
        <Text>지원하지 않는 쇼핑몰입니다.</Text>
      </Container>
    );
  }

  return (
    <Container noBody={true}>
      {/* 검색 필터 */}
      <Grid
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={10}
        bottom={20}
        top={40}
        style={{
          flexFlow: 'row wrap',
        }}
      >
        {/* 검색 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '10px',
            flexFlow: 'row wrap',
          }}
        >
          {/* 조회 상품상태 */}
          <Select
            label="상품상태"
            {...register('searchStatus', {
              onChange: () => {
                setValue('nowPage', 1); // 상품상태 변경 시 첫 페이지로 이동
              },
            })}
            variant="outlined"
            width={148}
          >
            <option value={SearchStatusType.ALL}>전체</option>
            <option value={SearchStatusType.UPDATE_SUCCESS}>업데이트 성공</option>
            <option value={SearchStatusType.UPDATE_FAILURE}>업데이트 실패</option>
            <option value={SearchStatusType.REGIST_SUCCESS}>등록 성공</option>
            <option value={SearchStatusType.REGIST_FAILURE}>등록 실패</option>
          </Select>
          {/* 조회 키 */}
          <Select label="검색어" {...register('searchType')} variant="outlined" width={160}>
            {config.searchOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          {/* 조회 값 */}
          <TextField
            type="text"
            placeholder="조회 할 내용"
            variant="outlined"
            {...register('searchValue')}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSubmit(onSubmit)();
              }
            }}
          />
          <Button
            variant="outlined"
            color="main"
            size="small"
            width={57}
            height={35}
            borderRadius={4}
            onClick={handleSubmit(onSubmit)}
            style={{
              width: '57px',
              padding: '7px 3px',
            }}
          >
            검색
          </Button>
        </div>

        {/* 일괄 업데이트 버튼 (선택된 상품이 있으면 선택 업데이트, 없으면 일괄 업데이트) */}
        <Button
          color={selectedRowIds.length === 0 && 1 - (requestCountAllData?.requestCount || 0) === 0 ? 'gray' : 'main'}
          variant="contained"
          width={150}
          height={35}
          size="small"
          borderRadius={4}
          onClick={handleBatchUpdate}
          style={{
            padding: '7px 3px',
          }}
        >
          {selectedRowIds.length > 0 ? <>선택 업데이트</> : <>일괄 업데이트 ({1 - (requestCountAllData?.requestCount || 0)}/1)</>}
        </Button>
      </Grid>
      {/* 테이블 */}
      <Grid style={{ position: 'relative' }}>
        <RefetchTimeContainer display="flex" justifyContent="flex-end" alignItems="center">
          <Text fontSize="0.75rem" color="#999">
            마지막 새로고침: {refetchElapsedTime}
          </Text>
        </RefetchTimeContainer>
        <Table
          rowHeight={60}
          isAutoRowHeight={true}
          showRowCount={true}
          allRowCount={totalCount}
          checkboxSelection={true}
          selectedRowIds={selectedRowIds}
          selectedRowCount={selectedRowIds.length}
          rowUniqueIds={trackingList?.map((product) => product.listId) || []}
          onSelectionChange={(selectedRows) => setSelectedRowIds(selectedRows)}
          loading={isTrackingListFetching}
          noRowsLabel={searchQuery.searchValue ? `'${searchQuery.searchValue}' 검색 결과가 존재하지 않습니다.` : '상품이 존재하지 않습니다.'}
          headerBackgroundColor="#F7F7F7"
          disableRowSelectionOnClick={true}
          headerItems={[
            {
              minWidth: 100,
              flex: 1,
              headerName: config.scrapProdCodeLabel,
              align: 'center',
              headerAlign: 'center',
              renderCell: (params: any) => {
                const scrapShopEtc2 = params.value?.scrapShopEtc2;
                const scrapProdCode = params.value?.scrapProdCode;
                const url = config.getScrapProductUrl(scrapShopEtc2 || '', scrapProdCode || '');
                return (
                  <a
                    style={{
                      textDecoration: url ? 'underline' : 'none',
                    }}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={url ? 0 : -1}
                  >
                    {scrapProdCode || '-'}
                  </a>
                );
              },
            },
            {
              minWidth: 180,
              flex: 1.8,
              headerName: config.scrapProdNameLabel,
              align: 'center',
              headerAlign: 'center',
              renderCell: (params: any) => {
                return (
                  <Text
                    style={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      padding: '12px 4px',
                      width: '100%',
                    }}
                  >
                    {params.value || '-'}
                  </Text>
                );
              },
            },
            {
              minWidth: 200,
              flex: 2,
              headerName: '실패 사유',
              align: 'center',
              headerAlign: 'center',
              renderCell: (params: any) => {
                let message = '-';
                try {
                  if (params.value) {
                    const value = typeof params.value === 'string' ? JSON.parse(params.value) : params.value;
                    message = value.message || '-';
                  }
                } catch (e) {
                  if (typeof params.value === 'string') {
                    const match = params.value.match(/"message"\s*:\s*"([^"]*)"/);
                    message = match ? match[1] : params.value;
                  } else {
                    message = '-';
                  }
                }
                return (
                  <Text
                    style={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      padding: '12px 4px',
                      width: '100%',
                    }}
                  >
                    {message}
                  </Text>
                );
              },
            },
            {
              minWidth: 100,
              flex: 1,
              headerName: config.wakeupProdCodeLabel,
              align: 'center',
              headerAlign: 'center',
              renderCell: (params: any) => {
                const url = config.getWakeupProductUrl(params.value || '');
                return (
                  <a
                    style={{
                      textDecoration: url ? 'underline' : 'none',
                    }}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={url ? 0 : -1}
                  >
                    {params.value || '-'}
                  </a>
                );
              },
            },
            {
              minWidth: 100,
              flex: 1,
              headerName: '상품 상태',
              align: 'center',
              headerAlign: 'center',
              renderCell: (params: any) => {
                const STATUS = {
                  [SearchStatusType.UPDATE_SUCCESS]: '업데이트 성공',
                  [SearchStatusType.UPDATE_FAILURE]: '업데이트 실패',
                  [SearchStatusType.REGIST_SUCCESS]: '등록 성공',
                  [SearchStatusType.REGIST_FAILURE]: '등록 실패',
                };
                return (
                  <Text color={params.value === SearchStatusType.UPDATE_SUCCESS || params.value === SearchStatusType.REGIST_SUCCESS ? '#1BAB66' : '#EB4F6B'}>
                    {STATUS[params.value as keyof typeof STATUS]}
                  </Text>
                );
              },
            },
            {
              minWidth: 150,
              flex: 1.5,
              headerName: '업데이트 일시',
              align: 'center',
              headerAlign: 'center',
              renderCell: (params: any) => {
                if (!params.value) return <Text>-</Text>;
                const date = new Date(params.value);
                const yyyy = date.getFullYear();
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                const hh = String(date.getHours()).padStart(2, '0');
                const min = String(date.getMinutes()).padStart(2, '0');
                const ss = String(date.getSeconds()).padStart(2, '0');
                const formatted = `${yyyy}/${mm}/${dd} ${hh}:${min}:${ss}`;
                return <Text>{formatted}</Text>;
              },
            },
            {
              minWidth: 100,
              flex: 1,
              headerName: '업데이트',
              align: 'center',
              headerAlign: 'center',
              renderCell: (params: any) => {
                const isDisabled = params.value?.status === SearchStatusType.REGIST_FAILURE;
                return (
                  <Button
                    variant={isDisabled ? 'contained' : 'outlined'}
                    color={isDisabled ? 'gray' : 'main'}
                    size="small"
                    width={100}
                    height={34}
                    borderRadius={4}
                    disabled={isDisabled}
                    onClick={() => handleSingleUpdate(params, isDisabled)}
                  >
                    업데이트
                  </Button>
                );
              },
            },
          ]}
          bodyItems={
            isTrackingListLoading
              ? []
              : trackingList?.map((product) => [
                  {
                    scrapShopEtc2: product.scrapShopEtc2,
                    scrapProdCode: product.scrapProdCode,
                  },
                  product.scrapProdInfo?.[0]?.name || '-',
                  product.lastFailReason || '-',
                  product.wakeupProdCode,
                  product.status,
                  product.updatedAt,
                  {
                    status: product.status,
                    productName: product.scrapProdInfo?.[0]?.name,
                    schedulerDetailId: product.schedulerDetailId,
                    wakeupProdCode: product.wakeupProdCode,
                    scrapProdCode: product.scrapProdCode,
                  },
                ])
          }
        />
        <Grid top={30} />
        {/* 페이지네이션은 데이터가 있을 때만 */}
        {totalCount > 0 && <Pagination nowPage={nowPage} max={maxPage} size={5} nowPageEvent={(page: number) => setValue('nowPage', page)} />}
      </Grid>
    </Container>
  );
}
